import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { ASSET_ICONS, DAMAGE_TYPES, formatDateTime } from '../utils';

const DAMAGE_STATUS_BADGE = {
  reported:    'badge-yellow',
  in_repair:   'badge-orange',
  repaired:    'badge-green',
  replaced:    'badge-blue',
  written_off: 'badge-gray',
};

export default function DamageReports() {
  const { withAuth } = useAuth();
  const navigate = useNavigate();
  const [reports, setReports] = useState([]);
  const [stats, setStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('reported');
  const [filterType, setFilterType] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      await withAuth(async () => {
        const res = await api.getDamageSummary({
          ...(filterStatus ? { status: filterStatus } : {}),
          ...(filterType ? { damageType: filterType } : {}),
        });
        setReports(res.data || []);
        setStats(res.stats || []);
      });
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [filterStatus, filterType, withAuth]);

  useEffect(() => { load(); }, [load]);

  return (
    <div>
      <div style={{ padding: '20px 28px', borderBottom: '2px solid var(--accent)', background: 'var(--bg-1)' }}>
        <h1 style={{ fontSize: 18, fontWeight: 600 }}>Damage & Loss Reports</h1>
        <p style={{ color: 'var(--text-2)', fontSize: 13, marginTop: 2 }}>
          All HALO tickets are created automatically on submission
        </p>
      </div>

      {/* Stats row */}
      {stats.length > 0 && (
        <div style={{ padding: '16px 28px', borderBottom: '1px solid var(--border)', display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          {stats.map(s => (
            <div key={s.damage_type} style={{
              background: 'var(--bg-2)', borderRadius: 'var(--radius-sm)',
              padding: '8px 14px', display: 'flex', gap: 10, alignItems: 'center',
            }}>
              <span style={{ fontSize: 14, fontWeight: 500, textTransform: 'capitalize' }}>
                {s.damage_type?.replace(/_/g, ' ')}
              </span>
              <span className="badge badge-gray">{s.count} total</span>
              {parseInt(s.open) > 0 && <span className="badge badge-yellow">{s.open} open</span>}
            </div>
          ))}
        </div>
      )}

      {/* Filters */}
      <div style={{ padding: '14px 28px', borderBottom: '1px solid var(--border)', background: 'var(--bg-1)', display: 'flex', gap: 10 }}>
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={{ width: 160 }}>
          <option value="">All Status</option>
          <option value="reported">Reported</option>
          <option value="in_repair">In Repair</option>
          <option value="repaired">Repaired</option>
          <option value="replaced">Replaced</option>
          <option value="written_off">Written Off</option>
        </select>
        <select value={filterType} onChange={e => setFilterType(e.target.value)} style={{ width: 160 }}>
          <option value="">All Types</option>
          {DAMAGE_TYPES.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
        </select>
      </div>

      <div style={{ padding: '20px 28px' }}>
        <div className="card">
          {loading
            ? <div className="empty-state"><div className="spinner" /></div>
            : reports.length === 0
              ? <div className="empty-state">No damage reports found</div>
              : (
                <table>
                  <thead>
                    <tr>
                      <th>Device</th>
                      <th>Type</th>
                      <th>Description</th>
                      <th>Employee</th>
                      <th>Location</th>
                      <th>HALO Ticket</th>
                      <th>Reported</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reports.map(r => (
                      <tr key={r.id} style={{ cursor: 'pointer' }} onClick={() => navigate(`/assets/${r.asset_id || ''}`)}>
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <span style={{ fontSize: 16 }}>{ASSET_ICONS[r.asset_type] || '📦'}</span>
                            <div>
                              <div style={{ fontWeight: 500, fontSize: 14 }}>{r.display_name}</div>
                              <span className="mono">{r.asset_tag}</span>
                            </div>
                          </div>
                        </td>
                        <td><span className="chip">{r.damage_type?.replace(/_/g, ' ')}</span></td>
                        <td style={{ fontSize: 14, maxWidth: 220, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', color: 'var(--text-1)' }}>
                          {r.description}
                        </td>
                        <td style={{ fontSize: 14, color: 'var(--text-2)' }}>{r.employee_name || '—'}</td>
                        <td style={{ fontSize: 14, color: 'var(--text-2)' }}>{r.location || '—'}</td>
                        <td>
                          {r.halo_ticket_id
                            ? <span className="mono badge badge-blue">#{r.halo_ticket_id}</span>
                            : <span style={{ color: 'var(--text-2)', fontSize: 13 }}>—</span>
                          }
                        </td>
                        <td style={{ fontSize: 13, color: 'var(--text-2)' }}>{formatDateTime(r.reported_at)}</td>
                        <td>
                          <span className={`badge ${DAMAGE_STATUS_BADGE[r.damage_status] || 'badge-gray'}`}>
                            {r.damage_status?.replace(/_/g, ' ')}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )
          }
        </div>
      </div>
    </div>
  );
}
